import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { lerNotasMarkdown } from '../notas-markdown';

/**
 * Notas da versão escritas em Markdown no backoffice. Monta a tela com elementos do Angular a
 * partir dos blocos e trechos da leitura: nunca usa innerHTML, então HTML escrito nas notas chega
 * como texto e só existe link com http ou https.
 */
@Component({
  selector: 'app-notas-markdown',
  imports: [NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-3 text-sm text-content-secondary' },
  template: `
    <ng-template #trechos let-lista>
      @for (t of $any(lista); track $index) {
        @if (t.link) {
          <a
            class="text-color-brand underline underline-offset-2"
            [class.font-semibold]="t.negrito"
            [class.italic]="t.italico"
            [href]="t.link"
            target="_blank"
            rel="noopener noreferrer"
            >{{ t.texto }}</a
          >
        } @else {
          <span [class.font-semibold]="t.negrito" [class.text-content-primary]="t.negrito" [class.italic]="t.italico">{{
            t.texto
          }}</span>
        }
      }
    </ng-template>

    @for (bloco of blocos(); track $index) {
      @if (bloco.tipo === 'titulo') {
        <h5 class="mt-2 text-xs font-semibold uppercase tracking-wide text-content-tertiary first:mt-0">
          <ng-container *ngTemplateOutlet="trechos; context: { $implicit: bloco.trechos }" />
        </h5>
      } @else if (bloco.tipo === 'paragrafo') {
        <p>
          @for (linha of bloco.linhas; track $index) {
            @if (!$first) {
              <br />
            }
            <ng-container *ngTemplateOutlet="trechos; context: { $implicit: linha }" />
          }
        </p>
      } @else if (bloco.tipo === 'lista' && bloco.ordenada) {
        <ol class="flex flex-col gap-2">
          @for (item of bloco.itens; track $index) {
            <li class="flex gap-2.5">
              <span class="shrink-0 tabular-nums text-content-tertiary" aria-hidden="true"
                >{{ bloco.inicio + $index }}.</span
              >
              <span class="min-w-0"><ng-container *ngTemplateOutlet="trechos; context: { $implicit: item }" /></span>
            </li>
          }
        </ol>
      } @else if (bloco.tipo === 'lista') {
        <ul class="flex flex-col gap-2">
          @for (item of bloco.itens; track $index) {
            <li class="flex gap-2.5">
              <span class="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-color-brand/70" aria-hidden="true"></span>
              <span class="min-w-0"><ng-container *ngTemplateOutlet="trechos; context: { $implicit: item }" /></span>
            </li>
          }
        </ul>
      }
    }
  `,
})
export class NotasMarkdownComponent {
  readonly markdown = input.required<string>();
  protected readonly blocos = computed(() => lerNotasMarkdown(this.markdown()));
}
